import React, { createContext, useContext, useEffect } from 'react'
import { EarnType } from '~/lib/constants'
import {
	EarnHook,
	EarnStatus,
	useEarnContract,
} from '~/contracts/useEarnContract'
import { useWallet, WalletStatus } from '~/lib/useWallet'
import { getChainById, getEarnByType } from '~/lib/config'
import { StaticJsonRpcProvider } from '@ethersproject/providers'

const EarnContext = createContext<{
	contract: EarnHook
	earnType: EarnType
	isWrongChain: boolean
	changeNetwork: () => Promise<void>
}>(null)

export const useEarns = () => useContext(EarnContext)

export const useEarn = () => useContext(EarnContext)

export const EarnProvider: React.FC<{ earnType: EarnType }> = ({
	earnType,
	children,
}) => {
	const wallet = useWallet()

	const earnConfig = getEarnByType(earnType)
	const chainConfig = getChainById(earnConfig.chainId)

	const requireChainId = earnConfig.chainId
	const isCurrentChain = wallet.isCurrentChain(requireChainId)

	const provider = isCurrentChain
		? wallet.web3
		: new StaticJsonRpcProvider(chainConfig.rpc)

	const earnContract = useEarnContract({
		account: wallet.account,
		chainConfig,
		earnConfig,
		provider,
		status: wallet.status,
	})

	const onBlock = (_block: number) =>
		earnContract.status == EarnStatus.SUCCESS && isCurrentChain
			? earnContract.refresh()
			: Promise.resolve()

	useEffect(() => {
		if (earnContract.status == EarnStatus.SUCCESS) {
			provider.on('block', onBlock)
			return () => {
				provider.off('block', onBlock)
			}
		}
	}, [earnContract.status, provider, onBlock])

	useEffect(() => {
		if (!wallet.account) return

		earnContract.load()
	}, [earnType, wallet.account])

	const changeNetwork = () => {
		const chain = getChainById(requireChainId)

		if (!isCurrentChain) {
			return wallet
				.setChain(requireChainId)
				.catch(() => wallet.addChain(chain))
		} else {
			return Promise.resolve()
		}
	}

	const isWrongChain =
		wallet.status === WalletStatus.CONNECTED && !isCurrentChain

	return (
		<EarnContext.Provider
			value={{
				earnType,
				contract: earnContract,
				isWrongChain,
				changeNetwork,
			}}
			children={children}
		/>
	)
}
